import { Spacing } from '@persona-id/design-tokens';
import { capitalize } from 'lodash';
import { DraggableProvided } from 'react-beautiful-dnd';
import { Controller, UseFormReturn } from 'react-hook-form';
import serviceConfigurations from 'shared/verifications/international_one_config.json';
import styled from 'styled-components';

import { DataSourceConfigurationPanelForm } from 'dashboard/components/DataSourceConfigurationTable/DataSourceConfigurationPanel';
import ReactSelect from 'dashboard/components/ReactSelect';
import {
  InternationalTwoPlusTwoOneConfig,
  TwoPlusTwoAccounts,
} from 'dashboard/models/vendor-config';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';
import useToggle from 'lib/hooks/useToggle';

const Section = styled.section`
  padding: ${Spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const StyledLabel = styled(Label)`
  margin-bottom: ${Spacing[2]};
  display: inline-block;
`;

interface InternationTwoPlusTwoOnePanelProps {
  formReturn: UseFormReturn<DataSourceConfigurationPanelForm, object>;
  countryCode: string;
  index: number;
  draggableProvided: DraggableProvided;
}

interface CountryConfiguration {
  default_service: string;
  verifiable_fields: Record<string, boolean>;
  data_sources: ServiceConfiguration[];
}
interface ServiceConfiguration {
  adult_population_coverage: number;
  consent_required: boolean;
  data_source_name: string;
  data_source_type: string;
}

const InternationalTwoPlusTwoOnePanel = ({
  formReturn,
  countryCode,
  index,
  draggableProvided,
}: InternationTwoPlusTwoOnePanelProps) => {
  const { formState, control } = formReturn;
  const { errors } = formState;
  useToggle(false);

  let possibleServiceNames: string[] = [];

  if (
    (serviceConfigurations as Record<string, CountryConfiguration>)[
      countryCode
    ] as CountryConfiguration
  ) {
    const countryConfig: CountryConfiguration = (
      serviceConfigurations as Record<string, CountryConfiguration>
    )[countryCode];
    possibleServiceNames = (countryConfig['data_sources'] as ServiceConfiguration[])?.map(
      (service: ServiceConfiguration) => service['data_source_name'],
    );
  }

  const serviceOptions = (possibleServiceNames || []).map((serviceName) => ({
    label: serviceName,
    value: serviceName,
  }));

  const accountOptions = (Object.values(TwoPlusTwoAccounts) || []).map((accountName) => ({
    label: capitalize(accountName.split('_').join(' ')),
    value: accountName,
  }));

  return (
    <>
      <Section>
        <StyledLabel>Service</StyledLabel>
        <Controller
          name={`configurations.${index}.configuration.0.data-source`}
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <div
              data-rbd-drag-handle-context-id={
                draggableProvided.dragHandleProps?.['data-rbd-drag-handle-context-id']
              }
              data-rbd-drag-handle-draggable-id='ignore-drag'
              style={{
                cursor: 'auto',
              }}
            >
              <ReactSelect
                id={`configurations.${index}.configuration.0.data-source`}
                onChange={onChange}
                isSearchable={true}
                onBlur={onBlur}
                value={value}
                controlShouldRenderValue={true}
                placeholder='Select services'
                hideSelectedOptions={false}
                hasError={
                  !!(
                    errors?.configurations?.[index]
                      ?.configuration as InternationalTwoPlusTwoOneConfig[]
                  )?.[0]['data-source']
                }
                options={serviceOptions}
                portal
                isDisabled={false}
              />
            </div>
          )}
        />
        <Spacer height={1.5} />
      </Section>

      <Section>
        <StyledLabel>Account</StyledLabel>
        <Controller
          name={`configurations.${index}.configuration.0.account-name`}
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <div
              data-rbd-drag-handle-context-id={
                draggableProvided.dragHandleProps?.['data-rbd-drag-handle-context-id']
              }
              data-rbd-drag-handle-draggable-id='ignore-drag'
              style={{
                cursor: 'auto',
              }}
            >
              <ReactSelect
                id={`configurations.${index}.configuration.0.account-name`}
                onChange={onChange}
                isSearchable={true}
                onBlur={onBlur}
                value={value}
                controlShouldRenderValue={true}
                placeholder='Select services'
                hideSelectedOptions={false}
                hasError={
                  !!(
                    errors?.configurations?.[index]
                      ?.configuration as InternationalTwoPlusTwoOneConfig[]
                  )?.[0]['account-name']
                }
                options={accountOptions}
                portal
                isDisabled={false}
              />
            </div>
          )}
        />
        <Spacer height={1.5} />
      </Section>
    </>
  );
};

export default InternationalTwoPlusTwoOnePanel;
